import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectOptionType } from '../../../../../components/dialog/helper';
import { useInstallationContext } from '../../../../../context/installation';
import { TariffKindInternal } from '../../../../../data-access/gql-types/graphql';
import { useGetTariffs } from '../../../hooks/tariffs/use-get-tariffs';
import { getTariffDataByEnum } from '../../channel-configuration/utils';
import { TariffName, TariffOperator } from '../../channel-details/optimizer/configuration/types/tariff-kind';
import { tariffKind, tariffsForRequest } from '../../channel-details/optimizer/configuration/utils/tariff-kind';
import { TariffPrefix } from '../../common/tariff/types';
import { formatHour } from '../../common/tariff/utils';
import { TariffAdditionalData } from '../types/types';

export const useTariffs = () => {
  const { t } = useTranslation('channel-details');
  const { allTariffs } = useGetTariffs();
  const [tariff, setTariff] = useState<TariffName | null>(null);
  const [operator, setOperator] = useState<TariffOperator | null>(null);
  const { selectedInstallation } = useInstallationContext();
  const [tariffData, setTariffData] = useState<TariffAdditionalData>({
    hoursRanges: [],
    wholeHolidaysCheaper: false,
    wholeWeekendCheaper: false,
    ignoreSummerTimeChanges: false,
  });

  const fillTariffData = (operator: TariffOperator, tariff: TariffName) => {
    setOperator(operator);
    setTariff(tariff);

    const kind = tariffsForRequest.find((x) => x.operator === operator && x.tariff === tariff)?.enum;

    if (kind) {
      const tariffData = getTariffDataByEnum(allTariffs, kind as TariffKindInternal);

      setTariffData({
        wholeHolidaysCheaper: tariffData?.wholeHolidaysCheaper || false,
        wholeWeekendCheaper: tariffData?.wholeWeekendCheaper || false,
        ignoreSummerTimeChanges: tariffData?.ignoreSummerTimeChanges || false,
        hoursRanges:
          tariffData?.hoursRanges.map((x) => ({ ...x, from: formatHour(x.from), to: formatHour(x.to) })) || [],
      });
    }
  };

  useEffect(() => {
    if (selectedInstallation?.previewTariff) {
      const tariff = tariffKind[selectedInstallation.previewTariff];
      fillTariffData(tariff.operator as TariffOperator, tariff.tariff as TariffName);
    }
  }, [selectedInstallation?.previewTariff, allTariffs]);

  const tariffs: SelectOptionType<TariffName>[] = useMemo(() => {
    const tariffs =
      allTariffs
        .find((x) => x.operator.toLowerCase() === operator?.toLowerCase())
        ?.tariffs.filter((x) => x.name !== TariffPrefix.G13S)
        .map((y) => ({
          label: y.name as TariffPrefix,
          value: y.name.toLowerCase() as TariffName,
        })) || [];

    return [
      ...tariffs,
      ...(operator === TariffOperator.Tauron
        ? [{ label: t('optimizer.configuration.tariff.g13s'), value: TariffName.G13S }]
        : []),
      { label: t('optimizer.configuration.tariff.dynamic'), value: TariffName.Dynamic },
    ];
  }, [allTariffs, operator]);

  const handleTariffChange = (tariff: TariffName) => {
    if (!operator) return;
    fillTariffData(operator, tariff);
  };

  return {
    operator,
    tariff,
    tariffs,
    tariffData,
    handleTariffChange,
    setOperator,
  };
};
